import logger from "../utils/logger";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { changeToken, selectUser, setUser } from "../slices/userReducer";
import { supabase } from "../supabaseClient";
import i18n from "i18next";
import { initializeVisitorTracking, trackPageViewAnalytics } from "../utils/visitorTracking";
import { initPromoterTracking } from "../utils/promoterTracking";
import { initTokenTracking, attributeTokenSignup } from "../utils/tokenTracking";
import { getProfileAttributionFields } from "../utils/utmTracking";
import useButtonTracking from "./useButtonTracking";

export const useAppInit = () => {
  const [eventsLoading, setEventsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [eventData, setEventData] = useState([]);
  const [spinWinEvents, setSpinWinEvents] = useState([]);
  const [prefetchedTokenFilters, setPrefetchedTokenFilters] = useState(null);
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || navigator.language.split("-")[0]
  );

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const getTokenFromUrl = () => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("token");
  };

  const fetchToken = () => {
    const token = getTokenFromUrl() || localStorage.getItem("token");
    if (token) {
      dispatch(changeToken(token));
    } else {
      dispatch(setUser({ loading: false }));
    }
  };

  useEffect(() => {
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);
  }, [language]);

  const eventsFetchedForUserRef = useRef(null);
  const initialAuthDoneRef = useRef(false);
  const userIdRef = useRef(user.id);
  const eventDataRef = useRef(eventData);
  userIdRef.current = user.id;
  eventDataRef.current = eventData;

  useEffect(() => {
    if (eventsFetchedForUserRef.current === (user.id || '__anonymous__')) return;

    const isAdminRoute = location.pathname.startsWith('/admin');
    const isPartnerRoute = location.pathname.startsWith('/partner-dashboard');
    if (isAdminRoute || isPartnerRoute) {
      eventsFetchedForUserRef.current = user.id || '__anonymous__';
      setEventsLoading(false);
      setDealsLoading(false);
      return;
    }

    eventsFetchedForUserRef.current = user.id || '__anonymous__';

    const getEvents = async () => {
      let isAdmin = false;
      if (user?.id) {
        try {
          const { checkIsAdminSync } = await import('../helpers/helpers');
          isAdmin = await checkIsAdminSync();
        } catch (error) {
          logger.error('Error checking admin status:', error);
        }
      }

      let query = supabase
        .from("Events")
        .select(`*, venue:Venue (id, name), event_artists:EventArtists (artist:Artist (name))`)
        .eq("is_spin_win", false)
        .order("datetime", { ascending: false })
        .limit(500);

      if (!isAdmin) {
        query = query.or("admin_only_visibility.is.null,admin_only_visibility.eq.false");
      }

      const events = await query;
      if (events.error) {
        logger.error('Failed to fetch events:', events.error);
        // Don't overwrite existing data if the fetch fails (e.g. expired auth token)
        return;
      }
      setEventData(events.data || []);
    };

    const getSpinWinEvents = async () => {
      let isAdmin = false;
      if (user?.id) {
        try {
          const { checkIsAdminSync } = await import('../helpers/helpers');
          isAdmin = await checkIsAdminSync();
        } catch (error) {
          logger.error('Error checking admin status:', error);
        }
      }

      let query = supabase
        .from("Events")
        .select("*, venue:Venue(*)")
        .eq("is_spin_win", true)
        .eq("spin_win_live", true)
        .order("spin_win_display_order", { ascending: true });

      if (!isAdmin) {
        query = query.or("admin_only_visibility.is.null,admin_only_visibility.eq.false");
      }

      const { data, error } = await query;

      if (error) {
        logger.error("Error fetching spin & win events:", error);
        // Don't clear existing data on auth error
      } else {
        setSpinWinEvents(data || []);
      }
    };

    const getDeals = async () => {
      try {
        const { data, error } = await supabase.from("Deals").select("*").limit(200);
        if (error) {
          logger.error("Failed to fetch deals:", error);
          return;
        }
        setDeals(data || []);
      } finally {
        // Deal links (/deal?deal_id=X) wait for this flag before deciding a deal
        // does not exist, so it must be cleared even when the request fails.
        setDealsLoading(false);
      }
    };

    const getTokenDefaultFilters = async () => {
      const urlToken = new URLSearchParams(window.location.search).get("token");
      const activeToken = urlToken || localStorage.getItem("token");
      if (!activeToken) return null;
      try {
        const { data } = await supabase
          .from("Token")
          .select("default_filters")
          .eq("name", activeToken)
          .eq("active", true)
          .maybeSingle();
        return data?.default_filters || null;
      } catch {
        return null;
      }
    };

    Promise.all([
      getDeals().catch((err) => { logger.error('Failed to fetch deals:', err); }),
      getEvents().catch((err) => { logger.error('Failed to fetch events:', err); }),
      getSpinWinEvents().catch((err) => { logger.error('Failed to fetch spin-win events:', err); }),
      getTokenDefaultFilters().then(setPrefetchedTokenFilters).catch(() => null),
    ]).finally(() => {
      setEventsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, location.pathname]);

  // Safety net: never let the events spinner hang indefinitely. If any fetch in
  // the events effect stalls (supabase-js has no default timeout), the .finally
  // never runs and eventsLoading stays true forever. Force it false after a
  // generous window; late event data still renders when it arrives.
  useEffect(() => {
    const t = setTimeout(() => setEventsLoading(false), 12000);
    return () => clearTimeout(t);
  }, []);

  // Last-resort self-healing: if the app still has no events after a generous
  // window (hung fetch, unhandled bad state), clear the known-troublesome
  // localStorage keys and reload exactly once. Normal users never trigger this;
  // it only fires when the page would otherwise sit loading forever.
  useEffect(() => {
    const RECOVERY_KEY = "22night_auto_recovered";
    if (sessionStorage.getItem(RECOVERY_KEY)) return;
    const t = setTimeout(() => {
      if (eventDataRef.current && eventDataRef.current.length > 0) return;
      try {
        // Only clear filter/personalization keys that can hang the render.
        // The Supabase session is deliberately NOT cleared here — auth-js
        // already handles dead sessions, and this path must never log out a
        // valid user who is just on a slow connection.
        ["token", "eventFilters", "city", "lastAppliedToken", "userSetFilters"].forEach(
          (k) => { try { localStorage.removeItem(k); } catch {} }
        );
        sessionStorage.setItem(RECOVERY_KEY, "1");
      } catch {}
      window.location.reload();
    }, 20000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initializeVisitorTracking();
  }, []);

  useEffect(() => {
    initPromoterTracking(dispatch);
  }, []);

  useEffect(() => {
    initTokenTracking();
  }, []);

  useButtonTracking({
    enabled: true,
    debug: process.env.NODE_ENV === 'development',
    debounceMs: 150,
    batchSize: 3,
    excludeClasses: ['no-track', 'ignore-click', 'carousel-control']
  });

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 8000);

    const cleanupExpiredSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        // getSession() auto-refreshes an expired session; an error here means
        // the refresh token itself is dead (e.g. InvalidJWTToken on the exp
        // claim), so the session is unrecoverable. Clear it instead of
        // retrying the same dead session on every page load.
        if (error) {
          await supabase.auth.signOut();
          return;
        }
        if (session?.access_token) {
          try {
            const payload = JSON.parse(atob(session.access_token.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
              await supabase.auth.signOut();
            }
          } catch {}
        }
      } catch {}
    };

    supabase.auth
      .getUser()
      .then(async (res) => {
        const auth_user = res.data.user;
        
        if (!auth_user) {
          await cleanupExpiredSession();
          setIsLoading(false);
          fetchToken();
          trackPageViewAnalytics("GENERAL", null);
          return;
        }
        
        const user_info = auth_user.user_metadata;
        const social_user =
          auth_user.app_metadata.provider === "google" ||
          auth_user.app_metadata.provider === "apple";

        setTimeout(() => {
          setIsLoading(false);
        }, 100);

        trackPageViewAnalytics("GENERAL", auth_user.id);

        // Check for User record and auto-create if needed — MUST complete
        // before setUserInfoId runs so the User row is available to query.
        const userByAuthId = await supabase
          .from("User")
          .select("*")
          .eq("auth_user_id", auth_user.id)
          .limit(1)
          .single();

        if (userByAuthId.error) {
          const potential_user = await supabase
            .from("User")
            .select("*")
            .ilike("email", auth_user.email)
            .limit(1)
            .single();

          if (potential_user.data && !potential_user.data.auth_user_id) {
            const updateRes = await supabase
              .from("User")
              .update({ auth_user_id: auth_user.id })
              .eq("id", potential_user.data.id);
            if (updateRes.error) {
              logger.error("Failed to backfill auth_user_id", updateRes.error);
            }
          }

          if (!potential_user.data) {
            const hasCompletedForm = user_info?.first_name && 
                                     user_info?.last_name && 
                                     user_info?.phone_number;
            
            if (hasCompletedForm && !social_user) {
              const { data, error } = await supabase.from("User").insert({
                email: auth_user.email,
                phone_number: user_info.phone_number,
                auth_user_id: auth_user.id,
                first_name: user_info.first_name,
                last_name: user_info.last_name,
                date_of_birth: user_info.date_of_birth,
                gender: user_info.gender,
                location: user_info.location,
                genres: localStorage.getItem("genres"),
                ...getProfileAttributionFields(),
              });

              if (error) {
                logger.error("[useAppInit] Failed to auto-create User record", error);
              }

              const promoterRef = localStorage.getItem('promoter_ref');
              const promoterRefTs = localStorage.getItem('promoter_ref_timestamp');
              if (promoterRef && promoterRefTs) {
                const daysSince = (Date.now() - parseInt(promoterRefTs, 10)) / (1000 * 60 * 60 * 24);
                if (daysSince <= 7) {
                  await supabase.rpc('attribute_promoter_signup', { p_referral_code: promoterRef, p_user_id: auth_user.id });
                }
                localStorage.removeItem('promoter_ref');
                localStorage.removeItem('promoter_ref_timestamp');
              }

              await attributeTokenSignup(auth_user.id);

            } else {
              const currentPath = location.pathname;
              const excludedPaths = ['/create-account', '/choose-genres', '/verify-email', '/reset-password', '/set-password', '/password-forgot'];
              const isEventPage = currentPath.startsWith('/event/') || currentPath.startsWith('/events');
              const isCheckoutPage = currentPath.startsWith('/checkout');
              
              if (!excludedPaths.includes(currentPath) && !isEventPage && !isCheckoutPage) {
                const fullPath = currentPath + location.search;
                const redirectUrl = currentPath !== '/' ? encodeURIComponent(fullPath) : '/events';
                navigate(`/create-account?email=${auth_user.email}&redirectUrl=${redirectUrl}`);
              }
            }
          }
        }

        if (!res.error) {
          const setUserInfoId = async () => {
            const response = await supabase
              .from("User")
              .select("*")
              .eq("email", auth_user.email)
              .limit(1)
              .single();

            dispatch(
              setUser({
                loading: false,
                id: auth_user.id,
                email: user_info.email,
                phone_number: user_info.phone_number,
                first_name: user_info.first_name,
                last_name: user_info.last_name,
                date_of_birth: user_info.date_of_birth,
                location: user_info.location,
                gender: user_info.gender,
                created_at: user_info.created_at,
                token: user_info.token,
                role: auth_user.role,
                genres: user_info.genres,
                user_info_id: response.data?.id,
              })
            );

            const urlToken = getTokenFromUrl();
            if (urlToken) {
              dispatch(changeToken(urlToken));
            }
          };

          setUserInfoId();

        } else {
          fetchToken();
          trackPageViewAnalytics("GENERAL", null);
        }
      })
      .catch(async (err) => {
        await cleanupExpiredSession();
        setIsLoading(false);
        fetchToken();
        trackPageViewAnalytics("GENERAL", null);
      })
      .finally(() => {
        clearTimeout(loadingTimeout);
        initialAuthDoneRef.current = true;
      });

    return () => clearTimeout(loadingTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!initialAuthDoneRef.current) return;

        if (event === "SIGNED_IN" && session?.user) {
          const auth_user = session.user;

          if (userIdRef.current === auth_user.id) return;

          const user_info = auth_user.user_metadata;

          const response = await supabase
            .from("User")
            .select("*")
            .eq("email", auth_user.email)
            .limit(1)
            .single();

          dispatch(
            setUser({
              loading: false,
              id: auth_user.id,
              email: user_info.email || auth_user.email,
              phone_number: user_info.phone_number || "",
              first_name: user_info.first_name || "",
              last_name: user_info.last_name || "",
              date_of_birth: user_info.date_of_birth || "",
              location: user_info.location || "",
              gender: user_info.gender || "",
              created_at: user_info.created_at || "",
              token: user_info.token || "",
              role: auth_user.role || "",
              genres: user_info.genres || "",
              user_info_id: response.data?.id || "",
            })
          );
        }

        if (event === "SIGNED_OUT") {
          dispatch(
            setUser({
              loading: false,
              id: "",
              email: "",
              phone_number: "",
              first_name: "",
              last_name: "",
              date_of_birth: "",
              location: "",
              gender: "",
              created_at: "",
              token: "",
              role: "",
              genres: "",
              user_info_id: "",
            })
          );
        }

        // If token just refreshed and we have no event data (previous fetch failed),
        // clear the ref guard so the next navigation/render triggers a re-fetch
        if (event === "TOKEN_REFRESHED" && eventDataRef.current.length === 0) {
          eventsFetchedForUserRef.current = null;
        }
      }
    );

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    eventsLoading,
    setEventsLoading,
    isLoading,
    eventData,
    spinWinEvents,
    prefetchedTokenFilters,
    deals,
    dealsLoading,
    language,
    setLanguage,
    user,
    location
  };
};
